import {
  DEFAULT_LANDING_SEO,
  INDEXATION_MODE,
  REVIEW_STATUS,
} from './defaultSeo'
import { FULL_LANDING_SEO_FIELDS, SEO_TEXT_FIELDS } from './seoProfiles'

const EMPTY_HTML_RE = /^<p>\s*(?:<br\s*\/?>)?\s*<\/p>$/i

const REVIEW_STATUS_VALUES = Object.values(REVIEW_STATUS)
const INDEXATION_MODE_VALUES = Object.values(INDEXATION_MODE)

/**
 * Normalize a string SEO field: trim; whitespace-only / empty HTML → null.
 * Does not mutate the input.
 */
export function normalizeSeoString(value) {
  if (value === null || value === undefined) return null
  if (typeof value !== 'string') return value

  const trimmed = value.trim()
  if (!trimmed) return null
  if (EMPTY_HTML_RE.test(trimmed)) return null

  const withoutTags = trimmed.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
  if (!withoutTags) return null

  return trimmed
}

export function normalizeReviewStatus(value) {
  if (REVIEW_STATUS_VALUES.includes(value)) return value
  return DEFAULT_LANDING_SEO.reviewStatus
}

export function normalizeIndexationMode(value) {
  if (INDEXATION_MODE_VALUES.includes(value)) return value
  return DEFAULT_LANDING_SEO.indexationMode
}

function assertFieldsAllowlist(fields) {
  if (!Array.isArray(fields) || fields.length === 0) {
    throw new Error('SEO fields allowlist is required')
  }
}

function normalizeFieldValue(field, source) {
  switch (field) {
    case 'schemaVersion':
      return 1
    case 'title':
    case 'metaDescription':
    case 'ogTitle':
    case 'ogDescription':
    case 'h1':
    case 'introText':
    case 'bottomText':
      return normalizeSeoString(source[field])
    case 'ogImage':
      return source.ogImage === undefined ? null : source.ogImage
    case 'reviewStatus':
      return normalizeReviewStatus(source.reviewStatus)
    case 'indexationMode':
      return normalizeIndexationMode(source.indexationMode)
    case 'updated':
      return source.updated === undefined ? null : source.updated
    default:
      return undefined
  }
}

/**
 * Build a known SEO object from form/local values for the given allowlist.
 * Source values are not mutated. Unknown keys are not included.
 *
 * @param {object} input
 * @param {{ fields?: string[] }} [options] — defaults to full landing fields
 */
export function normalizeSeoValues(input = {}, { fields = FULL_LANDING_SEO_FIELDS } = {}) {
  assertFieldsAllowlist(fields)
  const source = input && typeof input === 'object' ? input : {}
  const result = {}

  fields.forEach((field) => {
    result[field] = normalizeFieldValue(field, source)
  })

  return result
}

/**
 * Merge document.seo with defaults for form init (legacy-safe).
 * Only seeds fields present in the allowlist.
 *
 * @param {object|null|undefined} docSeo
 * @param {{ fields?: string[] }} [options]
 */
export function seoFormDefaultsFromDoc(docSeo, { fields = FULL_LANDING_SEO_FIELDS } = {}) {
  assertFieldsAllowlist(fields)
  const normalized = normalizeSeoValues(docSeo || {}, { fields })
  const formData = { ...normalized }

  fields.forEach((field) => {
    if (SEO_TEXT_FIELDS.includes(field)) {
      formData[field] = normalized[field] ?? ''
    }
  })

  return formData
}
